import React, { useEffect, useState } from 'react';
import { Tooltip } from 'antd';
import type { TooltipPlacement } from 'antd/es/tooltip';
import {
  CopyOutlined,
  DeleteOutlined,
  DiffOutlined,
  DownloadOutlined,
  FolderOpenOutlined,
  ShareAltOutlined,
} from '@ant-design/icons';
import { RevButton } from '../Button';
import { useLanguage } from '../../contexts/LanguageContext';
import styles from './ActionButtons.module.scss';
import { isCoarsePointerDevice } from '../../utils/videoFullscreen';

/** Footer bar (z-index ~900) üstünde tooltip */
const FOOTER_ACTION_TT_Z = 1100;

const ActionTooltip: React.FC<{
  label: string;
  /** Disabled düğmede hover için span sarıcı (Ant önerisi) */
  showOnDisabled?: boolean;
  placement?: TooltipPlacement;
  enabled?: boolean;
  children: React.ReactElement;
}> = ({ label, showOnDisabled, placement = 'top', enabled = true, children }) => {
  if (!enabled) {
    return showOnDisabled ? (
      <span className={styles.actionTooltipWrap}>{children}</span>
    ) : (
      children
    );
  }

  return (
    <Tooltip
      title={label}
      placement={placement}
      autoAdjustOverflow
      mouseEnterDelay={0.12}
      getPopupContainer={() => document.body}
      styles={{ root: { zIndex: FOOTER_ACTION_TT_Z, maxWidth: 'min(100vw - 24px, 240px)' } }}
    >
      {showOnDisabled ? (
        <span className={styles.actionTooltipWrap}>{children}</span>
      ) : (
        children
      )}
    </Tooltip>
  );
};

interface ActionButtonsProps {
  // Permissions
  canDelete: boolean;
  canShare: () => boolean;
  canDownload: () => boolean;
  canCompare: () => boolean;
  canMultiSelect: () => boolean;
  
  // Data
  selectedItems?: Set<number>;
  fileDetail?: any;
  currentEntry?: any;
  
  // Handlers
  onDelete: () => void;
  onCompare: () => void;
  onCopyUrl?: () => void; // Artık kullanılmıyor; Copy Link Share modal içinde
  onDownload?: () => void;
  onShare: () => void;
  onMove?: () => void;
  onDuplicate?: () => void;
  
  // Options
  hideDownload?: boolean;
  downloadLoading?: boolean;
  /** Share sadece sağ tık ve ... menüsünde; footer'da gösterme */
  showShare?: boolean;
  /** Move/Duplicate yalnızca proje/klasör çoklu seçim footer'ında gösterilir */
  showMoveDuplicate?: boolean;
  /** Move/Duplicate yetkisi */
  canMoveOrDuplicate?: boolean;
  moveLabel?: string;
  duplicateLabel?: string;
  deleteLabel?: string;
  downloadLabel?: string;
}

const ActionButtons: React.FC<ActionButtonsProps> = ({
  canDelete,
  canShare,
  canDownload,
  canCompare,
  canMultiSelect,
  selectedItems,
  fileDetail,
  currentEntry,
  onDelete,
  onCompare,
  onCopyUrl: _onCopyUrl,
  onDownload,
  onShare,
  onMove,
  onDuplicate,
  hideDownload,
  downloadLoading,
  showShare = true,
  showMoveDuplicate = false,
  canMoveOrDuplicate = false,
  moveLabel,
  duplicateLabel,
  deleteLabel,
  downloadLabel,
}) => {
  const { t } = useLanguage();
  const [tooltipsEnabled, setTooltipsEnabled] = useState(() => !isCoarsePointerDevice());

  useEffect(() => {
    const mq = window.matchMedia('(hover: none) and (pointer: coarse)');
    const sync = () => setTooltipsEnabled(!mq.matches);
    sync();
    mq.addEventListener('change', sync);
    return () => mq.removeEventListener('change', sync);
  }, []);

  const resolvedMoveLabel = moveLabel ?? t('projects.move') ?? 'Move';
  const resolvedDuplicateLabel = duplicateLabel ?? t('projects.duplicate') ?? 'Duplicate';
  const resolvedDeleteLabel = deleteLabel ?? t('common.delete') ?? 'Delete';
  const resolvedDownloadLabel = downloadLabel ?? t('fileDetail.download') ?? 'Download';

  const isMultiSelectDisabled = () => {
    return selectedItems && selectedItems.size > 1 && !canMultiSelect();
  };
  const hasSelection = !!selectedItems && selectedItems.size > 0;
  const moveDisabled = !hasSelection || isMultiSelectDisabled();
  const duplicateDisabled = !hasSelection || isMultiSelectDisabled();
  const deleteDisabled =
    ((!selectedItems || selectedItems.size === 0) && !fileDetail && !currentEntry) ||
    isMultiSelectDisabled();
  const downloadDisabled =
    (!selectedItems || selectedItems.size === 0) && !fileDetail && !currentEntry;

  return (
    <div className={styles.buttonsContainer}>
      {showMoveDuplicate && canMoveOrDuplicate && onMove && (
        <ActionTooltip
          label={resolvedMoveLabel}
          showOnDisabled={moveDisabled}
          placement="topLeft"
          enabled={tooltipsEnabled}
        >
          <RevButton
            className={styles.iconOnlyAction}
            icon={<FolderOpenOutlined />}
            onClick={onMove}
            disabled={moveDisabled}
            aria-label={resolvedMoveLabel}
          />
        </ActionTooltip>
      )}

      {showMoveDuplicate && canMoveOrDuplicate && onDuplicate && (
        <ActionTooltip
          label={resolvedDuplicateLabel}
          showOnDisabled={duplicateDisabled}
          enabled={tooltipsEnabled}
        >
          <RevButton
            className={styles.iconOnlyAction}
            icon={<CopyOutlined />}
            onClick={onDuplicate}
            disabled={duplicateDisabled}
            aria-label={resolvedDuplicateLabel}
          />
        </ActionTooltip>
      )}

      {canDelete && (
        <ActionTooltip
          label={resolvedDeleteLabel}
          showOnDisabled={deleteDisabled}
          enabled={tooltipsEnabled}
        >
          <RevButton
            className={styles.iconOnlyAction}
            icon={<DeleteOutlined />}
            onClick={onDelete}
            disabled={deleteDisabled}
            aria-label={resolvedDeleteLabel}
          />
        </ActionTooltip>
      )}

      {canDownload() && !hideDownload && (
        <ActionTooltip
          label={resolvedDownloadLabel}
          showOnDisabled={downloadDisabled}
          placement="topRight"
          enabled={tooltipsEnabled}
        >
          <RevButton
            className={styles.downloadAction}
            icon={<DownloadOutlined />}
            onClick={onDownload}
            disabled={downloadDisabled}
            loading={downloadLoading}
            aria-label={resolvedDownloadLabel}
          >
            <span className={styles.downloadLabel}>{resolvedDownloadLabel}</span>
          </RevButton>
        </ActionTooltip>
      )}

      {canCompare() && (
        <RevButton
          className={styles.compareButton}
          icon={<DiffOutlined />}
          onClick={onCompare}
          disabled={isMultiSelectDisabled()}
        >
          Compare
        </RevButton>
      )}

      {canShare() && showShare && (
        <RevButton
          icon={<ShareAltOutlined />}
          className={`${styles.shareButton} ${styles.shareAction}`}
          onClick={onShare}
          disabled={(!selectedItems || selectedItems.size === 0) || (selectedItems && selectedItems.size > 1)}
          aria-label="Share"
          title="Share"
        >
          <span className={styles.shareLabel}>Share</span>
        </RevButton>
      )}
    </div>
  );
};

export default ActionButtons; 